/** @format */
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineShoppingCart } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { add_To_card } from "../../store/reducers/cardReducers";
import { devise, trackEvent, TruncateText } from "../../utils/utils";
import Ratings from "../Ratings";

const rankColors = {
  0: { bg: "bg-yellow-400", text: "text-yellow-900", label: "🥇" },
  1: { bg: "bg-gray-300", text: "text-gray-800", label: "🥈" },
  2: { bg: "bg-amber-600", text: "text-white", label: "🥉" },
};

const TopSellers = ({ products = [] }) => {
  const { t } = useTranslation();
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({
      left: dir === "left" ? -340 : 340,
      behavior: "smooth",
    });
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { customerInfo } = useSelector((state) => state.auth);
  //const { successMessage, messageError } = useSelector((state) => state.card);

  const add_card = (id) => {
    if (customerInfo) {
      dispatch(
        add_To_card({
          customerId: customerInfo.id,
          productId: id,
          quantity: 1,
        }),
      );
    } else {
      navigate("/login");
    }
  };
  /*
  const add_wishlist = (product) => {
    if (customerInfo) {
      dispatch(
        add_To_wishlist({
          customerId: customerInfo.id,
          productId: product._id,
          name: product.name,
          price: product.price,
          image: product.images[0],
          discount: product.discount,
          rating: product.rating,
          slug: product.slug,
        }),
      );
    }
  };
*/
  const getDiscountedPrice = (price, discount) =>
    Math.floor((parseFloat(price) * (100 - discount)) / 100);

  if (!products?.length) return null;

  return (
    <div className='relative'>
      {/* Scroll buttons */}
      <button
        onClick={() => scroll("left")}
        className='absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-md border border-gray-100 text-gray-500 hover:text-indigo-600 hover:border-indigo-200 transition-all'
      >
        <ChevronLeft size={18} />
      </button>
      <button
        onClick={() => scroll("right")}
        className='absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-md border border-gray-100 text-gray-500 hover:text-indigo-600 hover:border-indigo-200 transition-all'
      >
        <ChevronRight size={18} />
      </button>

      {/* Scrollable list */}
      <div
        ref={scrollRef}
        className='flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-2'
        style={{ scrollbarWidth: "none" }}
      >
        {products?.map((product, i) => {
          const rank = rankColors[i];
          const finalPrice = product?.discount
            ? getDiscountedPrice(product?.price, product?.discount)
            : product?.price;

          return (
            <Link
              key={product?._id || i}
              to={`/product/details/${product?._id}`}
              onClick={() => trackEvent("view", product)}
              className='group flex-shrink-0 flex items-center gap-4 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:border-indigo-100 transition-all duration-300 p-3 w-[300px] sm:w-[320px]'
            >
              {/* Rank Badge + Image */}
              <div className='relative flex-shrink-0'>
                {/* Rank badge */}
                {rank && (
                  <span
                    className={`absolute -top-2 -left-2 z-10 w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold shadow-md ${rank.bg} ${rank.text}`}
                  >
                    {i < 3 ? rank.label : `#${i + 1}`}
                  </span>
                )}
                {!rank && (
                  <span className='absolute -top-2 -left-2 z-10 w-7 h-7 flex items-center justify-center rounded-full text-[10px] font-bold shadow-sm bg-gray-100 text-gray-500'>
                    #{i + 1}
                  </span>
                )}

                {/* Image */}
                <div className='w-[90px] h-[90px] rounded-xl overflow-hidden bg-gray-50 border border-gray-100'>
                  <img
                    src={product?.images?.[0]?.url || product?.images?.[0]}
                    alt={product?.name}
                    className='w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-400'
                    loading='lazy'
                  />
                </div>
              </div>

              {/* Infos */}
              <div className='flex flex-col gap-1.5 flex-1 min-w-0'>
                <TruncateText
                  className='text-sm font-semibold text-gray-800 group-hover:text-indigo-600 transition-colors leading-snug'
                  text={product?.name}
                  maxLength={45}
                />

                {/* Rating */}
                <div className='flex items-center gap-1'>
                  <Ratings ratings={product?.rating} />
                </div>

                {/* Price */}
                <div className='flex items-baseline gap-2 flex-wrap'>
                  <span className='text-base font-bold text-gray-900'>
                    {finalPrice} {devise}
                  </span>
                  {product?.discount > 0 && (
                    <>
                      <span className='text-xs text-gray-400 line-through'>
                        {product?.price} {devise}
                      </span>
                      <span className='text-[10px] font-bold text-white bg-red-500 px-1.5 py-0.5 rounded-full'>
                        -{product?.discount}%
                      </span>
                    </>
                  )}
                </div>

                {/* Cart button */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    trackEvent("cart", product); // 🔥 AJOUT
                    add_card(product?._id);
                  }}
                  className='mt-1 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 active:scale-95 transition-all w-fit'
                >
                  <AiOutlineShoppingCart size={13} />
                  {t("Add to cart")}
                </button>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default TopSellers;
